import styled from '@emotion/styled';
import { mediaQuery, Spacings } from '@styles/index';
import React from 'react';
import useDemoClasses from '@api/hooks/batch/useDemoClasses';
import { formatClassDetail } from '@cont/LearnerClass/utils';
import { getLink } from '@models/Item';
import ClassCard from 'aquilla/core/organisms/ClassCard';
import Link from '@base/Link/Link';
import Typography from 'aquilla/core/atoms/Typography';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { segment } from '@actions/analytics';
import { usePageInfo } from '@stores/AppCommonData';
import { getCurrentPageName } from '@stores/selectors/common';
import DemoClassesCarousel from './NewCarousel';

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  grid-column: span 24;
  position: relative;
  margin-bottom: ${Spacings.SPACING_10B};
  ${mediaQuery.uptoMobile} {
    display: block;
    grid-column: span 10;
    grid-template-columns: repeat(10, 1fr);
    margin-bottom: ${Spacings.SPACING_6B};
  }
`;

const LeftWrapper = styled.div`
  grid-column: 2/8;
  position: sticky;
  top: 235px;
  z-index: 1;
  ${mediaQuery.uptoMobile} {
    position: relative;
    top: 0;
    padding: 0;
    grid-column: span 10;
    margin-bottom: ${Spacings.SPACING_6B};
  }
`;

const RightWrapper = styled.div`
  grid-column: 9/25;
  grid-template-columns: repeat(2, 2fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  grid-row-gap: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    grid-column: span 10;
    grid-template-columns: repeat(1, 1fr);
    & > a {
      grid-column: span 10;
      margin-bottom: 0;
    }
  }
`;

const StyledTypo = styled(Typography)`
  font-weight: bold;
  margin-bottom: ${Spacings.SPACING_2B};

  ${mediaQuery.uptoMobile} {
    margin-bottom: 2px;
  }
`;

const DemoClasses = ({ apiKey, goalUID }) => {
  const { demoClasses = [], isError } = useDemoClasses(apiKey);
  const currentPageName = usePageInfo(getCurrentPageName);
  const goalSegmantData = useGoalEventProps({ goalUID });
  const demoClassesWithSession = demoClasses.reduce((acc, curr) => {
    if (curr.nextSession) return [...acc, curr];
    return acc;
  }, []);
  const handleClassCardClick = () => {
    segment.track('Demo Classes Card Clicked', {
      ...goalSegmantData,
      last_primary_source: currentPageName,
      last_primary_source_section: 'Demo Classes Section'
    });
  };

  if (isError || demoClassesWithSession.length === 0) return null;

  return (
    <Wrapper>
      <LeftWrapper>
        <StyledTypo variant="h5">Demo classes</StyledTypo>
        <Typography variant="p2" color="var(--color-text-secondary)">
          {' '}
          Watch free classes by the educators of this batch
        </Typography>
      </LeftWrapper>
      <RightWrapper>
        <DemoClassesCarousel>
          {demoClassesWithSession
            .map((classInfo) => {
              if (!classInfo.nextSession) return null;
              const as = getLink({
                ...classInfo,
                nextTrack: {
                  ...classInfo.nextSession,
                  ...classInfo.nextSession?.properties
                }
              });
              return (
                <Link passHref key={classInfo.uid} href={as}>
                  <ClassCard
                    title="Special class title"
                    topicColor="#8873FF"
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...formatClassDetail({
                      classInfo,
                      hideDetailsLine: false,
                      onClick: handleClassCardClick
                    })}
                  />
                </Link>
              );
            })
            .filter((item) => item !== null)}
        </DemoClassesCarousel>
      </RightWrapper>
    </Wrapper>
  );
};

export default DemoClasses;
